import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { type Observable } from 'rxjs';

import { SharedService } from './shared.service';

export interface MenuItem {
  name: string;
  icon: string;
  link?: string;
  roles?: string[];
  /** When true, user must have all roles in `roles`; otherwise having any one role is enough. */
  /** USE LIKE THIS IN ROUTING - data: { roles: ['Admin', 'Manager'], rolesRequireAll: true }, where rolesRequireAll will check for all the roles to be present. */
  rolesRequireAll?: boolean;
  children?: MenuItem[];
  queryParams?: Record<string, any>;
  kiIndustryType?: string; // Optional field to specify industry type
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private http = inject(HttpClient);
  private sharedService = inject(SharedService);

  getMenuItems(): Observable<MenuItem[]> {
    return this.http.get<MenuItem[]>(`assets/i18n/${this.sharedService.kiIndustryTypeOrDefault.toLowerCase()}/menu/menu-items.json`);
  }

  filterMenuItemsByRole(items: MenuItem[], userRoles: string[]): MenuItem[] {
    return items
      .filter(item => this.itemHasAccess(item, userRoles))
      .map(item => ({
        ...item,
        children: item.children ? this.filterMenuItemsByRole(item.children, userRoles) : undefined
      }));
  }

  private itemHasAccess(item: MenuItem, userRoles: string[]): boolean {
    if (item.roles?.length === 0) {
      return true;
    }
    if (item.roles) {
      const roles = item.roles;
      const hasRoleAccess = item.rolesRequireAll ? roles.every(role => userRoles.includes(role)) : roles.some(role => userRoles.includes(role));
      if (hasRoleAccess) {
        return item.kiIndustryType ? item.kiIndustryType.toUpperCase() === this.sharedService.kiIndustryType.toUpperCase() : true;
      }
    }
    if (item.children) {
      return item.children.some(child => this.itemHasAccess(child, userRoles));
    }
    return false;
  }
}
